import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { eq, not, or } from 'tio-ui/utilities';

import { registerDestructor } from '@ember/destroyable';
import FileUploader from 'tio-ui/components/file-uploader';
import Button from 'tio-reimagined/components/button';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import type { UploadFile, Queue } from 'ember-file-upload';
import type ObservablesService from 'tio-common/services/observables';
import type { ObservableDocumentAttributes } from 'tio-common/services/observables';
import type CableService from 'tio-common/services/cable';
import type ObservableDocumentModel from 'tio-common/models/observable-document';
import type { IntlService } from 'ember-intl';
import type PersonModel from 'tio-common/models/person';
import type Owner from '@ember/owner';
import {
  PhFileText,
  PhCloudArrowUp,
  PhCheckCircle,
  PhWarning,
  PhXCircle,
  PhFile,
  PhShieldCheck,
  PhChartLineUp,
  PhLightning,
  PhInfo,
  PhCircleNotch,
} from 'ember-phosphor-icons';

const ACCEPTED_FILE_TYPES = '.pdf,.png,.jpg,.jpeg,.txt';
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const VALID_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'txt'];

type UploadState = 'idle' | 'file-selected' | 'uploading' | 'processing' | 'success' | 'error';

type BatchStatusAttributes = Partial<ObservableDocumentAttributes> & {
  extractionState?: ObservableDocumentModel['extractionState'];
  reflectionState?: ObservableDocumentModel['reflectionState'];
};

type BatchStatus = {
  id: string;
  recordAttributes: BatchStatusAttributes;
  transition: { event: string };
};

interface InlineDocumentUploadSignature {
  Args: {
    person: PersonModel;
  };
}

const getFileExtension = (filename: string) => {
  return filename.split('.').pop()?.toLowerCase() ?? '';
};

const PROCESSING_TIMEOUT_MS = 60_000;

export default class InlineDocumentUpload extends Component<InlineDocumentUploadSignature> {
  @service declare observables: ObservablesService;
  @service declare cable: CableService;
  @service declare intl: IntlService;
  @service declare fileQueue: Queue;

  @tracked state: UploadState = 'idle';
  @tracked upload?: UploadFile;
  @tracked fileValidationError: string | null = null;
  @tracked uploadError: string | null = null;
  @tracked processingTimedOut = false;

  batchId = crypto.randomUUID();
  private subscriptionParams: object | null = null;
  private processingTimer?: ReturnType<typeof setTimeout>;

  constructor(owner: Owner, args: InlineDocumentUploadSignature['Args']) {
    super(owner, args);
    registerDestructor(this, () => {
      this.unsubscribe();
      clearTimeout(this.processingTimer);
    });
  }

  get queue() {
    // @ts-expect-error findOrCreate exists on Queue
    return this.fileQueue.findOrCreate('slr-inline-upload');
  }

  private unsubscribe = () => {
    if (this.subscriptionParams) {
      this.cable.unsubscribe(this.subscriptionParams);
      this.subscriptionParams = null;
    }
  };

  private subscribeToBatchStatus = () => {
    const channelParams = {
      channel: 'ObservableDocumentBatchStatusChannel',
      batch: this.batchId,
    };

    this.subscriptionParams = channelParams;

    this.cable.subscribe(channelParams, {
      received: (data: unknown) => {
        const msg = data as BatchStatus;
        const { id, recordAttributes, transition } = msg;

        this.observables.updateObservableDocument({ ...recordAttributes, id });

        const extractionFailed = ['ExtractionState.FAILED', 'ExtractionState.INVALID'].includes(
          recordAttributes.extractionState ?? ''
        );

        if (extractionFailed) {
          this.unsubscribe();
          clearTimeout(this.processingTimer);
          const isInvalidDocument = recordAttributes.extractionState === 'ExtractionState.INVALID';
          this.uploadError = isInvalidDocument
            ? this.intl.t('observability.wizard.upload.error_invalid_document')
            : this.intl.t('observability.wizard.upload.error_failed');
          this.state = 'error';
          return;
        }

        const reflectionSuccessful = transition.event === 'content_reflected';
        const reflectionFailed = transition.event === 'reflection_failed';

        if (reflectionSuccessful || reflectionFailed) {
          this.unsubscribe();
          clearTimeout(this.processingTimer);

          if (reflectionSuccessful) {
            this.state = 'success';
          } else {
            this.uploadError = this.intl.t('observability.wizard.upload.error_failed');
            this.state = 'error';
          }
        }
      },
    });

    this.processingTimer = setTimeout(() => {
      if (this.state === 'processing') {
        this.processingTimedOut = true;
      }
    }, PROCESSING_TIMEOUT_MS);
  };

  handleFileAdded = (uploadFile: UploadFile) => {
    this.fileValidationError = null;
    this.uploadError = null;
    const file = uploadFile.file;

    const ext = getFileExtension(file.name);
    if (!VALID_EXTENSIONS.includes(ext)) {
      this.fileValidationError = this.intl.t('observability.wizard.upload.invalid_file_type');
      this.queue.remove(uploadFile);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.fileValidationError = this.intl.t('observability.wizard.upload.file_too_large', {
        fileSize: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
        maxSize: MAX_FILE_SIZE_MB,
      });
      this.queue.remove(uploadFile);
      return;
    }

    this.upload = uploadFile;
    this.state = 'file-selected';
  };

  removeFile = () => {
    this.queue.remove(this.upload);
    this.upload = undefined;
    this.fileValidationError = null;
    this.state = 'idle';
  };

  handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!this.upload) return;

    this.unsubscribe();
    clearTimeout(this.processingTimer);
    this.processingTimedOut = false;
    this.state = 'uploading';
    this.subscribeToBatchStatus();

    try {
      await this.observables.createObservableDocument(this.upload, {
        person: this.args.person,
        batch: this.batchId,
      });

      this.state = 'processing';
    } catch {
      this.uploadError = this.intl.t('observability.wizard.upload.error_failed');
      this.state = 'error';
    }
  };

  reset = () => {
    this.unsubscribe();
    clearTimeout(this.processingTimer);
    this.queue.remove(this.upload);
    this.upload = undefined;
    this.fileValidationError = null;
    this.uploadError = null;
    this.processingTimedOut = false;
    this.state = 'idle';
    this.batchId = crypto.randomUUID();
  };

  <template>
    <div class="flex flex-col gap-5">
      {{#if (eq this.state "success")}}
        {{! ── Success State ── }}
        <div class="rounded-xl bg-emerald-50 border border-emerald-200 p-6 text-center">
          <div
            class="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 mx-auto mb-3"
          >
            <PhCheckCircle @size={{24}} class="text-emerald-600" />
          </div>
          <Heading @level={{3}} class="text-lg font-semibold text-emerald-900 mb-1">
            {{t "observability.wizard.complete.heading"}}
          </Heading>
          <Text class="text-sm text-emerald-700 mb-4">
            {{t
              "observability.wizard.complete.description"
              docType=(t "observability.wizard.doc_type.statement")
            }}
          </Text>
          <button
            type="button"
            class="text-sm font-medium text-emerald-700 underline hover:text-emerald-800"
            {{on "click" this.reset}}
          >
            {{t "observability.wizard.complete.upload_another"}}
          </button>
        </div>
      {{else if (eq this.state "processing")}}
        {{! ── Processing State ── }}
        <div class="rounded-xl border-2 border-indigo-200 bg-indigo-50 p-6">
          <div class="flex flex-col items-center text-center gap-3">
            <div class="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100">
              <PhCircleNotch @size={{24}} class="text-indigo-600 animate-spin" />
            </div>
            <div>
              <Heading @level={{3}} class="text-lg font-semibold text-indigo-900 mb-1">
                {{t "observability.wizard.upload.processing_title"}}
              </Heading>
              <Text class="text-sm text-indigo-700">
                {{t "observability.wizard.upload.processing_description"}}
              </Text>
            </div>
            {{#if this.upload}}
              <div class="flex items-center gap-2 bg-white/60 rounded-lg px-3 py-2 max-w-full">
                <PhFile @size={{16}} class="text-indigo-500 shrink-0" />
                <Text class="text-xs text-indigo-800 truncate min-w-0">
                  {{this.upload.file.name}}
                </Text>
              </div>
            {{/if}}
            {{#if this.processingTimedOut}}
              <div class="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-1 w-full">
                <div class="flex items-start gap-2">
                  <PhWarning @size={{16}} class="text-amber-500 shrink-0 mt-0.5" />
                  <Text class="text-xs text-amber-800">
                    {{t "observability.wizard.upload.processing_slow"}}
                  </Text>
                </div>
              </div>
            {{/if}}
          </div>
        </div>
      {{else}}
        {{! ── Statement Header ── }}
        <div class="flex items-center gap-2.5">
          <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100">
            <PhFileText @size={{20}} class="text-indigo-600" />
          </div>
          <Heading @level={{4}} class="text-base font-semibold text-gray-800">
            {{t "observability.wizard.choose.statement_title"}}
          </Heading>
        </div>

        {{! ── 3-Step Instructions ── }}
        <ol class="space-y-3">
          <li class="flex gap-3">
            <span
              class="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold shrink-0 mt-0.5"
            >{{1}}</span>
            <div>
              <Text class="font-medium text-gray-800 text-sm">
                {{t "observability.wizard.instructions.statement.step1_title"}}
              </Text>
              <Text class="text-xs text-gray-500 mt-0.5">
                {{t "observability.wizard.instructions.statement.step1_description"}}
              </Text>
            </div>
          </li>
          <li class="flex gap-3">
            <span
              class="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold shrink-0 mt-0.5"
            >{{2}}</span>
            <div>
              <Text class="font-medium text-gray-800 text-sm">
                {{t "observability.wizard.instructions.statement.step2_title"}}
              </Text>
              <Text class="text-xs text-gray-500 mt-0.5">
                {{t "observability.wizard.instructions.statement.step2_description"}}
              </Text>
            </div>
          </li>
          <li class="flex gap-3">
            <span
              class="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold shrink-0 mt-0.5"
            >{{3}}</span>
            <div>
              <Text class="font-medium text-gray-800 text-sm">
                {{t "observability.wizard.instructions.statement.step3_title"}}
              </Text>
              <Text class="text-xs text-gray-500 mt-0.5">
                {{t "observability.wizard.instructions.statement.step3_description"}}
              </Text>
            </div>
          </li>
        </ol>

        {{! ── What We Need Callout ── }}
        <div class="bg-indigo-50 rounded-xl p-3 flex items-start gap-2.5">
          <PhInfo @size={{18}} class="text-indigo-600 shrink-0 mt-0.5" />
          <Text class="text-xs text-indigo-800">
            <span class="font-semibold">
              {{t "observability.wizard.instructions.statement.what_we_need"}}
            </span>
            {{t "observability.wizard.instructions.statement.what_we_need_body"}}
          </Text>
        </div>

        {{! ── Why Upload Benefits ── }}
        <div class="flex flex-col gap-2">
          <Text class="text-xs font-semibold text-gray-600 uppercase tracking-wider">
            {{t "observability.wizard.choose.why_upload"}}
          </Text>
          <div class="flex flex-col gap-1.5">
            <div class="flex items-center gap-2">
              <PhLightning @size={{14}} class="text-amber-500 shrink-0" />
              <Text class="text-sm text-gray-700">
                {{t "observability.wizard.choose.benefit_contributions"}}
              </Text>
            </div>
            <div class="flex items-center gap-2">
              <PhChartLineUp @size={{14}} class="text-emerald-500 shrink-0" />
              <Text class="text-sm text-gray-700">
                {{t "observability.wizard.choose.benefit_tracking"}}
              </Text>
            </div>
            <div class="flex items-center gap-2">
              <PhShieldCheck @size={{14}} class="text-indigo-500 shrink-0" />
              <Text class="text-sm text-gray-700">
                {{t "observability.wizard.choose.benefit_forgiveness"}}
              </Text>
            </div>
          </div>
        </div>

        {{! ── Upload Area ── }}
        <form {{on "submit" this.handleSubmit}}>
          {{#if this.upload}}
            {{! File selected state }}
            <div class="rounded-xl border-2 border-indigo-200 bg-indigo-50 p-4 mb-3">
              <div class="flex items-center gap-3">
                <div
                  class="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100 shrink-0"
                >
                  <PhFile @size={{18}} class="text-indigo-600" />
                </div>
                <div class="flex-1 min-w-0 overflow-hidden">
                  <Text class="font-medium text-gray-900 truncate text-sm">
                    {{this.upload.file.name}}
                  </Text>
                  <Text class="text-xs text-indigo-600">
                    {{t "observability.wizard.upload.ready"}}
                  </Text>
                </div>
                <button
                  type="button"
                  class="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0"
                  {{on "click" this.removeFile}}
                >
                  <PhXCircle @size={{18}} />
                </button>
              </div>
            </div>
          {{else}}
            {{! Dropzone }}
            <FileUploader
              @name="slr-inline-upload"
              @accept={{ACCEPTED_FILE_TYPES}}
              @onFileAdded={{this.handleFileAdded}}
              @useDropzone={{true}}
            />
          {{/if}}

          {{! Validation error }}
          {{#if this.fileValidationError}}
            <div class="rounded-xl border border-red-200 bg-red-50 p-3 mt-3 flex items-start gap-2">
              <PhWarning @size={{16}} class="text-red-500 shrink-0 mt-0.5" />
              <Text class="text-xs text-red-800">{{this.fileValidationError}}</Text>
            </div>
          {{/if}}

          {{! Upload error }}
          {{#if (eq this.state "error")}}
            <div class="rounded-xl border border-red-200 bg-red-50 p-3 mt-3 flex items-start gap-2">
              <PhWarning @size={{16}} class="text-red-500 shrink-0 mt-0.5" />
              <div>
                <Text class="text-xs font-medium text-red-800">{{this.uploadError}}</Text>
                <button
                  type="button"
                  class="text-xs text-red-700 underline mt-1"
                  {{on "click" this.reset}}
                >
                  {{t "observability.wizard.upload.try_again"}}
                </button>
              </div>
            </div>
          {{/if}}

          {{! Submit button }}
          <div class="mt-3">
            <Button
              @intent="primary"
              type="submit"
              @isDisabled={{or (not this.upload) (eq this.state "uploading")}}
              @isRunning={{eq this.state "uploading"}}
              class="*:justify-center"
            >
              {{#if (eq this.state "uploading")}}
                {{t "observability.wizard.upload.uploading"}}
              {{else}}
                <span class="flex items-center gap-2">
                  <PhCloudArrowUp @size={{16}} />
                  {{t "slr.reimagined.upload_now"}}
                </span>
              {{/if}}
            </Button>
          </div>
        </form>
      {{/if}}
    </div>
  </template>
}
